import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { KeyMomentCategory } from './types';
import { keyMomentRowTouchesUnavailable } from './mergeKeyMomentsSameMinute';

import './KeyMoments.scss';

/**
 * Click targets on slits; hover preview is driven by PlayerUI `mousemove` on the bar.
 * With `hitTargetsOnly`, no visible chip (cuts use ProgressBar `gaps`).
 */
function KeyMomentsMarkers({
  items,
  duration,
  streamStartTimestamp,
  unavailableIds,
  previewIncidenceId,
  onMarkerClick,
  hitTargetsOnly,
}) {
  const markers = useMemo(() => {
    const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : 0;
    const start = streamStartTimestamp ?? 0;

    // Try to compute positions from timestamps. If the duration is unknown or
    // every item ends up clamped to the same edge (e.g. mock data whose
    // momentStart doesn't match the current programme), fall back to spreading
    // markers uniformly across the bar so the UI is still demoable.
    const usingTimestamps = safeDuration > 0 && start > 0;
    const computed = items.map((item, index) => {
      let position;
      if (usingTimestamps) {
        const eventOffset = item.timestamp - start;
        const raw = (eventOffset / safeDuration) * 100;
        position = Math.min(Math.max(raw, 0), 100);
      }
      return {
        incidenceId: item.incidenceId,
        category: item.category,
        cardColor: item.cardColor,
        moment: item.moment,
        position,
        rawValid: Number.isFinite(position) && position > 0 && position < 100,
        index,
      };
    });

    const anyValid = computed.some((m) => m.rawValid);
    if (anyValid) return computed;

    // Fallback: evenly distribute between 5% and 95%.
    const total = items.length > 0 ? items.length : 1;
    return computed.map((m, i) => ({
      ...m,
      position: 5 + ((i / Math.max(1, total - 1)) * 90),
    }));
  }, [items, duration, streamStartTimestamp]);

  const handleClick = useCallback((e, index) => {
    e.preventDefault();
    e.stopPropagation();
    const item = items[index];
    if (item && keyMomentRowTouchesUnavailable(item, unavailableIds)) return;
    if (item) onMarkerClick?.(item);
  }, [items, onMarkerClick, unavailableIds]);

  if (markers.length === 0) return null;

  return (
    <div
      className={classnames(
        'dtv-common-c-key-moments__markers',
        hitTargetsOnly && 'dtv-common-c-key-moments__markers--hit-only',
      )}
    >
      {markers.map((marker) => {
        const rowItem = items[marker.index];
        if (rowItem && keyMomentRowTouchesUnavailable(rowItem, unavailableIds)) {
          // Unavailable: only Slider `gaps` mask draws the cut; no DOM tick overlay.
          return null;
        }

        let markerCardTone = null;
        if (marker.category === KeyMomentCategory.CARD && marker.cardColor) {
          markerCardTone = marker.moment === 'RED_CARD_SECOND_YELLOW' ? 'yellow' : marker.cardColor;
        }
        const buttonClass = classnames(
          'dtv-common-c-key-moments__marker',
          `dtv-common-c-key-moments__marker--${marker.category}`,
          markerCardTone ? `dtv-common-c-key-moments__marker--card-${markerCardTone}` : null,
          marker.incidenceId === previewIncidenceId
            && 'dtv-common-c-key-moments__marker--cut-hover',
        );

        return (
          <button
            key={marker.incidenceId}
            type="button"
            className={buttonClass}
            style={{ left: `${marker.position}%` }}
            tabIndex={-1}
            aria-label={`Key moment ${items[marker.index]?.minutes ?? '?'}′`}
            onClick={(e) => handleClick(e, marker.index)}
          />
        );
      })}
    </div>
  );
}

KeyMomentsMarkers.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    incidenceId: PropTypes.number.isRequired,
    timestamp: PropTypes.number.isRequired,
    category: PropTypes.string.isRequired,
    moment: PropTypes.string,
    cardColor: PropTypes.string,
  })).isRequired,
  duration: PropTypes.number,
  streamStartTimestamp: PropTypes.number,
  unavailableIds: PropTypes.instanceOf(Set),
  previewIncidenceId: PropTypes.number,
  onMarkerClick: PropTypes.func,
  hitTargetsOnly: PropTypes.bool,
};

KeyMomentsMarkers.defaultProps = {
  duration: 0,
  streamStartTimestamp: 0,
  unavailableIds: undefined,
  previewIncidenceId: undefined,
  onMarkerClick: undefined,
  hitTargetsOnly: false,
};

export default memo(KeyMomentsMarkers);
